import React from 'react';
import { FiGlobe, FiTrendingUp, FiCheckCircle, FiClock, FiShield, FiZap,  FiUsers, FiAward } from 'react-icons/fi';

const CloudSwitch = () => {
  const reasons = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'India\'s Digital Infrastructure Is Ready',
      description: 'With 5G rollouts, affordable data, and widespread smartphone adoption, India is ready for cloud. Educational leaders recognize that the digital infrastructure now supports cloud-based solutions even in tier-2 and tier-3 cities.',
      stat: '900M+ internet users in India make cloud accessible everywhere',
      solution: 'Truyon is optimized for Indian networks, working seamlessly even in low-bandwidth areas.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'The Mobile-First Generation Is Here',
      description: 'Students, parents, and young faculty are digital natives. They expect to access everything from their phones. Cloud-based ERPs deliver this mobile-first experience that on-premise systems simply cannot match.',
      stat: '75% of Indian students access educational content on mobile',
      solution: 'Truyon is built mobile-first with a responsive interface for all devices.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Data Security Has Improved',
      description: 'Cloud providers now offer enterprise-grade security that most institutions cannot afford to implement on their own. Encryption, access controls, and compliance standards are built-in.',
      stat: '95% of cloud providers meet higher security standards than on-premise',
      solution: 'Truyon provides bank-grade security with encrypted data storage and access controls.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'No More IT Headaches',
      description: 'Educational leaders are tired of managing servers, fixing crashes, and scheduling updates. Cloud ERP eliminates the IT burden, allowing institutions to focus on education rather than technology maintenance.',
      stat: '80% of institutions reduce IT costs with cloud ERP',
      solution: 'Truyon is fully managed—no servers, no updates, no IT worries.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Hybrid Learning Is Here to Stay',
      description: 'The pandemic proved that hybrid learning is essential. Cloud-based systems enable seamless integration between online and offline education. Students can learn from anywhere, and teachers can teach from anywhere.',
      stat: '60% of institutions plan to continue hybrid learning models',
      solution: 'Truyon supports hybrid learning with accessible, collaborative features.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Cost Savings Are Too Significant to Ignore',
      description: 'In a sector where budgets are always tight, cloud ERP offers dramatic savings. No hardware purchases, no maintenance contracts, no IT staff increases. Pay only for what you use.',
      stat: 'Cloud ERP saves institutions 40-60% over 5 years',
      solution: 'Truyon starts at zero upfront cost, with affordable subscription plans.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Anywhere Access',
      description: 'Faculty, students, and parents can access the system from anywhere, anytime.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Instant Updates',
      description: 'New features and security patches are applied automatically—no downtime.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Cost Predictability',
      description: 'Budget-friendly subscription models with no surprise IT expenses.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Scalable for Growth',
      description: 'Add new users, institutions, and features effortlessly as you grow.',
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: 'Better Collaboration',
      description: 'Faculty and staff can collaborate in real-time, across campuses and locations.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Future-Ready',
      description: 'Cloud ERP prepares your institution for the future of education.',
      icon: <FiAward className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        In 2026, something significant is happening in Indian education. Educational leaders across the country—principals, directors, trustees—are making the switch to cloud-based ERP. They're moving away from on-premise systems that they've used for years, and embracing the cloud.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">This isn't a trend. It's a transformation.</strong> And it's happening because the cloud finally delivers what Indian institutions need—accessibility, affordability, security, and scalability. The reasons for switching are too compelling to ignore.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's why educational leaders are switching to cloud-based ERP in 2026.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">900M+</div>
          <div className="text-sm text-slate-500">Internet users in India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">75%</div>
          <div className="text-sm text-slate-500">Students access education on mobile</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">60%</div>
          <div className="text-sm text-slate-500">Plan to continue hybrid learning</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Leaders Are Making the Switch</h2>

      {/* Reasons List */}
      {reasons.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>How Truyon Helps</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Cloud ERP Delivers for Indian Institutions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Legacy vs. Cloud ERP in 2026</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Legacy On-Premise ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Access limited to campus</span></li>
              <li className="flex items-start gap-2">• <span>Manual updates and maintenance</span></li>
              <li className="flex items-start gap-2">• <span>High infrastructure costs</span></li>
              <li className="flex items-start gap-2">• <span>Difficult to scale</span></li>
              <li className="flex items-start gap-2">• <span>Limited collaboration</span></li>
              <li className="flex items-start gap-2">• <span>Slow feature updates</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Cloud ERP with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Accessible from anywhere</span></li>
              <li className="flex items-start gap-2">• <span>Automatic updates and maintenance</span></li>
              <li className="flex items-start gap-2">• <span>Affordable subscription model</span></li>
              <li className="flex items-start gap-2">• <span>Scale effortlessly</span></li>
              <li className="flex items-start gap-2">• <span>Real-time collaboration</span></li>
              <li className="flex items-start gap-2">• <span>Continuous innovation and features</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Decision Makers */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What Decision Makers Are Saying</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-sm text-slate-600 italic">"We spent years managing our on-premise system. The server crashes, the maintenance costs, the limited access. Switching to cloud was a relief. Now our teachers can access attendance from their phones, and I can see reports from anywhere."</p>
            <p className="text-xs font-semibold text-amber-700 mt-2">— Principal, Private School, Bangalore</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-sm text-slate-600 italic">"We were spending so much on IT infrastructure. Cloud ERP eliminated those costs. We now pay a predictable subscription and use the savings for student scholarships. It was an easy decision."</p>
            <p className="text-xs font-semibold text-amber-700 mt-2">— Trustee, Educational Trust, Mumbai</p>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Top Choice for Cloud ERP in India</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">☁️</div>
            <h4 className="font-bold text-slate-900 text-sm">100% Cloud-Based</h4>
            <p className="text-xs text-slate-600">No servers, no hardware, no IT burden</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🇮🇳</div>
            <h4 className="font-bold text-slate-900 text-sm">Built for India</h4>
            <p className="text-xs text-slate-600">Designed for Indian institutions and networks</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💰</div>
            <h4 className="font-bold text-slate-900 text-sm">Zero Upfront Cost</h4>
            <p className="text-xs text-slate-600">Free to start, affordable to grow</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First</h4>
            <p className="text-xs text-slate-600">Access from any device, anywhere</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Auto Updates</h4>
            <p className="text-xs text-slate-600">Always up-to-date with latest features</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Enterprise Security</h4>
            <p className="text-xs text-slate-600">Bank-grade security and compliance</p>
          </div>
        </div>
      </div>

      {/* The Future */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Future of Indian Education Is in the Cloud</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          The shift to cloud-based ERP in 2026 is not just about technology—it's about the future of education in India. Institutions that embrace the cloud are positioning themselves for growth, innovation, and success. They're preparing for hybrid learning, supporting mobile-first students, and operating with efficiency.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>The question is no longer "if" institutions will switch to cloud, but "when."</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Join the Shift to Cloud with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          In 2026, Indian educational leaders are making the switch to cloud-based ERP. They're leaving behind on-premise systems and embracing the accessibility, affordability, and scalability of the cloud.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is leading this transformation. 100% cloud-based. Built for India. Zero upfront cost. Mobile-first. Enterprise security. Auto updates. Free to start.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for cloud ERP.</strong> Join the institutions already making the shift.
        </p>
      </div>
    </>
  );
};

export default CloudSwitch;